import { apiSlice } from "../../app/apiSlice";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";

export interface UploadMediaResponse {
	bucket: string;
	key: string;
	contentType: string;
	url: string;
}

export interface UploadMediaArgs {
	file: File;
	/** S3-style prefix, e.g. `events` or `property-marketing` */
	folder: string;
}

function parseUploadMediaResponse(raw: unknown): UploadMediaResponse {
	const root = (raw ?? {}) as Record<string, unknown>;
	const inner =
		root.data && typeof root.data === "object" && !Array.isArray(root.data)
			? (root.data as Record<string, unknown>)
			: root;
	return {
		bucket: String(inner.bucket ?? ""),
		key: String(
			inner.key ?? inner.fileKey ?? inner.storageKey ?? inner.path ?? "",
		),
		contentType: String(inner.contentType ?? inner.mimeType ?? ""),
		url: String(inner.url ?? inner.fileUrl ?? ""),
	};
}

/** Resolve S3/storage key from upload API response (key field or URL path). */
export function resolveUploadStorageKey(res: UploadMediaResponse): string {
	const key = res.key?.trim();
	if (key) return key;
	const url = res.url?.trim();
	if (!url) return "";
	if (!url.startsWith("http")) return url.replace(/^\/+/, "");
	try {
		return new URL(url).pathname.replace(/^\/+/, "");
	} catch {
		return url.replace(/^\/+/, "");
	}
}

export const FOLDER_EVENTS = "events";
export const FOLDER_PROPERTY_MARKETING = "property-marketing";
export const FOLDER_PROPERTIES = "properties";
export const FOLDER_FNB = "fnb-restaurants";
export const FOLDER_CABANA = "cabanas";
export const FOLDER_STATIC = "static";
export const FOLDER_OTHERS = "others";
export const FOLDER_CHARGES_INVOICE_HEADER = "charges/invoice-header";
export const FOLDER_CHARGES_INVOICE_FOOTER = "charges/invoice-footer";
export const FOLDER_CHARGES_INVOICES = "charges/invoices";
export const FOLDER_REVENUE_STATEMENT_HEADER = "revenue/statement-header";
export const FOLDER_REVENUE_STATEMENTS = "revenue/statements";
export const FOLDER_MONTHLY_STATEMENT_HEADER = "monthly-statement/header";
export const FOLDER_MONTHLY_STATEMENTS = "monthly-statement/statements";

export const uploadApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		uploadMedia: builder.mutation<UploadMediaResponse, UploadMediaArgs>({
			query: ({ file, folder }) => {
				const body = new FormData();
				body.append("file", file);
				body.append("folder", folder);
				return {
					url: API_ENDPOINTS.upload.media,
					method: "POST",
					body,
				};
			},
			transformResponse: (raw: unknown) => parseUploadMediaResponse(raw),
		}),
	}),
	overrideExisting: false,
});

export const { useUploadMediaMutation } = uploadApi;
